import { useState, useEffect, Fragment } from "react";
import { Plus, Edit2, Eye, Trash2, Loader2, X, CheckCircle, AlertCircle, FileText, DollarSign, Clock, AlertTriangle, Search, FileDown, Calendar, Send, CheckCheck, MoreVertical, Paperclip, Upload, Download, FolderOpen } from "lucide-react";
import { adminService, Fatura, FaturaStats, Client, TipoDocumento } from "../../../services/adminService";
import { produtoServicoService, ProdutoServico } from "../../../services/propostaService";
import { documentService } from "../../../services/documentService";
import { ProdutoServicoSelector } from "../../../components/ProdutoServicoSelector";
import { PublicarPortalButton } from "../../../components/PublicarPortalButton";
import { formatInvoiceNumber } from "../../../utils/formatters";
import { toLocalISODate } from "../../../utils/dateUtils";

// State machine para controlar transições visíveis no modal
const TRANSICOES_FATURA: Record<string, string[]> = {
  rascunho: ['emitida', 'cancelada'],
  emitida: ['enviada', 'paga', 'cancelada', 'vencida'],
  enviada: ['paga', 'emitida', 'cancelada', 'vencida'],
  vencida: ['paga', 'emitida', 'cancelada'],
  paga: ['emitida', 'cancelada'],  // permite reverter pagamento incorreto
  cancelada: ['rascunho', 'emitida'], // pode reativar fatura cancelada
};

const statusLabels: Record<string, { label: string; color: string }> = {
  rascunho: { label: "Rascunho", color: "bg-muted text-foreground" },
  emitida: { label: "Emitida", color: "bg-blue-500/20 text-blue-700 dark:text-blue-400" },
  enviada: { label: "Enviada", color: "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400" },
  paga: { label: "Paga", color: "bg-green-500/20 text-green-700 dark:text-green-400" },
  cancelada: { label: "Cancelada", color: "bg-red-500/20 text-red-700 dark:text-red-400" },
  vencida: { label: "Vencida", color: "bg-orange-500/20 text-orange-700 dark:text-orange-400" },
};

const formasPagamento = [
  { value: "pix", label: "PIX" },
  { value: "boleto", label: "Boleto" },
  { value: "cartao", label: "Cartão" },
  { value: "transferencia", label: "Transferência" },
  { value: "dinheiro", label: "Dinheiro" },
];

export function FaturaManagement() {
  const [faturas, setFaturas] = useState<Fatura[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [produtosServicos, setProdutosServicos] = useState<ProdutoServico[]>([]);
  const [stats, setStats] = useState<FaturaStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingFatura, setEditingFatura] = useState<Fatura | null>(null);
  const [selectedFatura, setSelectedFatura] = useState<Fatura | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showProdutoDropdown, setShowProdutoDropdown] = useState<number | null>(null);
  const [gerandoMensal, setGerandoMensal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingFatura, setViewingFatura] = useState<Fatura | null>(null);
  const [statusMenuOpen, setStatusMenuOpen] = useState<string | null>(null);
  
  // Estados para Centralização Financeira
  const [expandedDocsFaturaId, setExpandedDocsFaturaId] = useState<string | null>(null);
  const [availableDocuments, setAvailableDocuments] = useState<any[]>([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);

  // Estados para anexar documento direto à fatura
  const [showAnexarModal, setShowAnexarModal] = useState(false);
  const [anexarFatura, setAnexarFatura] = useState<Fatura | null>(null);
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>([]);
  const [anexarFile, setAnexarFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [anexarForm, setAnexarForm] = useState({ tipoDocumentoId: "", dataReferencia: "", nome: "" });

  const [formData, setFormData] = useState({
    clientId: "", dataEmissao: "", dataVencimento: "", desconto: 0, acrescimo: 0,
    formaPagamento: "", observacoes: "", observacoesInternas: "",
    itens: [{ descricao: "", quantidade: 1, valorUnit: 0, adicionalExecucao: false, tipoItem: "proprio" as "proprio" | "terceiros" | "revenda", produtoServicoId: undefined as number | undefined }],
  });

  const [statusForm, setStatusForm] = useState({ status: "", valorPago: 0, dataPagamento: "" });

  useEffect(() => { loadData(); }, [selectedClientId, selectedStatus, currentPage]);

  useEffect(() => {
    // Carregar produtos/serviços quando abrir o modal
    if (showModal) {
      produtoServicoService.list({ active: true }).then(setProdutosServicos).catch(console.error);
    }
  }, [showModal]);

  // Fechar menu de status ao pressionar ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setStatusMenuOpen(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [faturasRes, clientsRes, statsRes] = await Promise.all([
        adminService.listFaturas({ 
          clientId: selectedClientId || undefined, 
          status: selectedStatus || undefined,
          page: currentPage,
          limit: 10,
        }),
        adminService.listClients(),
        adminService.getFaturaStats(selectedClientId || undefined),
      ]);
      setFaturas(faturasRes.data);
      setTotalPages(faturasRes.pagination?.totalPages || 1);
      setClients(clientsRes.data);
      setStats(statsRes);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setIsLoading(false);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const podeEditarDados = !editingFatura || ['rascunho', 'emitida'].includes(editingFatura.status);

      if (!editingFatura) {
        // Criar nova fatura
        const data = { ...formData, itens: formData.itens.filter(i => i.descricao.trim()) };
        await adminService.createFatura(data);
        setMessage({ type: "success", text: "Fatura criada com sucesso!" });
      } else if (podeEditarDados) {
        // Atualizar fatura existente (apenas rascunho ou emitida)
        const data = { ...formData, itens: formData.itens.filter(i => i.descricao.trim()) };
        await adminService.updateFatura(editingFatura.id, data);
        setMessage({ type: "success", text: "Fatura atualizada com sucesso!" });
      }

      closeModal();
      loadData();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao salvar fatura" });
    }
  };

  const handleStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFatura) return;
    try {
      await adminService.updateFaturaStatus(selectedFatura.id, statusForm.status,
        statusForm.status === "paga" ? { valorPago: statusForm.valorPago, dataPagamento: statusForm.dataPagamento } : undefined
      );
      setMessage({ type: "success", text: "Status atualizado!" });
      setShowStatusModal(false);
      loadData();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao atualizar status" });
    }
  };

  // Ação rápida: mudar status diretamente da tabela
  const handleQuickStatusChange = async (fatura: Fatura, novoStatus: string) => {
    try {
      // Se for marcar como paga, abre o modal para preencher dados
      if (novoStatus === 'paga') {
        openStatusModal(fatura);
        return;
      }
      
      await adminService.updateFaturaStatus(fatura.id, novoStatus);
      setMessage({ type: "success", text: `Status alterado para "${statusLabels[novoStatus]?.label}"` });
      setStatusMenuOpen(null);
      loadData();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao atualizar status" });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Deseja excluir esta fatura?")) return;
    try {
      await adminService.deleteFatura(id);
      setMessage({ type: "success", text: "Fatura excluída!" });
      loadData();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao excluir fatura" });
    }
  };

  const handleDeleteForced = async (fatura: Fatura) => {
    const statusLabel = statusLabels[fatura.status]?.label ?? fatura.status;
    if (!confirm(`Atenção: a fatura ${formatInvoiceNumber(fatura.numero)} está com status "${statusLabel}".\n\nDeseja mesmo excluí-la? Essa ação não pode ser desfeita.`)) return;
    try {
      await adminService.deleteFatura(fatura.id);
      setMessage({ type: "success", text: "Fatura excluída!" });
      loadData();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao excluir fatura" });
    }
  };

  const handleGerarMensal = async () => {
    const agora = new Date();
    const ref = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;
    if (!confirm(`Gerar faturas para todos os contratos ativos referente ao mês ${ref}?\nContratos que já possuem fatura para este mês serão ignorados.`)) return;
    try {
      setGerandoMensal(true);
      const resultado = await adminService.gerarFaturasMensal(ref);
      const msg = `✓ ${resultado.criadas} fatura(s) gerada(s) · ${resultado.ignoradas} já existia(m)` +
        (resultado.erros.length > 0 ? ` · ${resultado.erros.length} erro(s)` : '');
      setMessage({ type: resultado.erros.length > 0 ? 'error' : 'success', text: msg });
      loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Erro ao gerar faturas mensais' });
    } finally {
      setGerandoMensal(false);
    }
  };

  const openNewModal = () => {
    setEditingFatura(null);
    const hoje = toLocalISODate();
    setFormData({
      clientId: selectedClientId || "", dataEmissao: hoje,
      dataVencimento: hoje, desconto: 0, acrescimo: 0, formaPagamento: "", observacoes: "", observacoesInternas: "",
      itens: [{ descricao: "", quantidade: 1, valorUnit: 0, adicionalExecucao: false, tipoItem: "proprio", produtoServicoId: undefined }],
    });
    setShowModal(true);
  };

  const openEditModal = async (fatura: Fatura) => {
    const full = await adminService.getFatura(fatura.id);
    setEditingFatura(full);
    setFormData({
      clientId: full.clientId, dataEmissao: full.dataEmissao.split("T")[0],
      dataVencimento: full.dataVencimento.split("T")[0], desconto: full.desconto, acrescimo: full.acrescimo,
      formaPagamento: full.formaPagamento || "", observacoes: full.observacoes || "", observacoesInternas: full.observacoesInternas || "",
      itens: full.itens?.map(i => ({ descricao: i.descricao, quantidade: i.quantidade, valorUnit: i.valorUnit, adicionalExecucao: i.adicionalExecucao, tipoItem: i.tipoItem || "proprio", produtoServicoId: i.produtoServicoId })) || [],
    });
    setShowModal(true);
  };

  const openViewModal = async (fatura: Fatura) => {
    const full = await adminService.getFatura(fatura.id);
    setViewingFatura(full);
    setShowViewModal(true);
  };

  const openStatusModal = (fatura: Fatura) => {
    setSelectedFatura(fatura);
    const primeiraTransicao = (TRANSICOES_FATURA[fatura.status] ?? [])[0] ?? fatura.status;
    setStatusForm({ status: primeiraTransicao, valorPago: fatura.valorTotal, dataPagamento: toLocalISODate() });
    setShowStatusModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingFatura(null); setShowProdutoDropdown(null); };

  // Funções para Centralização Financeira
  const loadAvailableDocuments = async (fatura: Fatura) => {
    setLoadingDocumentos(true);
    try {
      const available = await adminService.listDocumentsByClient(fatura.clientId, {
        isNf: true,
        limit: 100,
        page: 1
      });
      setAvailableDocuments(available.data.filter((d: any) => !d.faturaId || d.faturaId === fatura.id));
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar documentos' });
    } finally {
      setLoadingDocumentos(false);
    }
  };

  const toggleDocumentos = (fatura: Fatura) => {
    if (expandedDocsFaturaId === fatura.id) {
      setExpandedDocsFaturaId(null);
      return;
    }
    setSelectedFatura(fatura);
    setExpandedDocsFaturaId(fatura.id);
    loadAvailableDocuments(fatura);
  };

  const handleLinkDocument = async (documentId: string) => {
    if (!selectedFatura) return;
    try {
      await adminService.linkDocumentToFatura(documentId, selectedFatura.id);
      setMessage({ type: 'success', text: 'Documento vinculado com sucesso!' });
      loadAvailableDocuments(selectedFatura);
      loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Erro ao vincular documento' });
    }
  };

  const handleUnlinkDocument = async (documentId: string) => {
    if (!selectedFatura) return;
    if (!confirm('Desvincular este documento da fatura?')) return;
    try {
      await adminService.linkDocumentToFatura(documentId, null);
      setMessage({ type: 'success', text: 'Documento desvinculado!' });
      loadAvailableDocuments(selectedFatura);
      loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Erro ao desvincular documento' });
    }
  };

  // Anexar documento direto à fatura
  const openAnexarModal = async (fatura: Fatura) => {
    setAnexarFatura(fatura);
    setAnexarFile(null);
    setAnexarForm({ tipoDocumentoId: "", dataReferencia: toLocalISODate(), nome: "" });
    setShowAnexarModal(true);
    if (tiposDocumento.length === 0) {
      try {
        const tipos = await adminService.listTiposDocumento();
        setTiposDocumento(tipos.filter((t: TipoDocumento) => t.active));
      } catch (error) {
        console.error('Erro ao carregar tipos de documento:', error);
      }
    }
  };

  const handleAnexarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setMessage({ type: 'error', text: 'Apenas arquivos PDF são permitidos' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Arquivo deve ter no máximo 10MB' });
      return;
    }
    setAnexarFile(file);
    if (!anexarForm.nome) {
      setAnexarForm((p) => ({ ...p, nome: file.name.replace('.pdf', '') }));
    }
  };

  const handleAnexarSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!anexarFatura) return;
    if (!anexarFile || !anexarForm.tipoDocumentoId || !anexarForm.dataReferencia) {
      setMessage({ type: 'error', text: 'Preencha todos os campos obrigatórios' });
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', anexarFile);
      data.append('clientId', anexarFatura.clientId);
      data.append('tipoDocumentoId', anexarForm.tipoDocumentoId);
      data.append('dataReferencia', anexarForm.dataReferencia);
      data.append('faturaId', anexarFatura.id);
      if (anexarForm.nome) data.append('nome', anexarForm.nome);

      await adminService.uploadDocument(data);
      setMessage({ type: 'success', text: 'Documento anexado à fatura com sucesso!' });
      setShowAnexarModal(false);
      setAnexarFatura(null);
      setAnexarFile(null);
      loadAvailableDocuments(anexarFatura);
      loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Erro ao anexar documento' });
    } finally {
      setUploading(false);
    }
  };

  const addItem = () => setFormData(p => ({ ...p, itens: [...p.itens, { descricao: "", quantidade: 1, valorUnit: 0, adicionalExecucao: false, tipoItem: "proprio", produtoServicoId: undefined }] }));
  const removeItem = (idx: number) => setFormData(p => ({ ...p, itens: p.itens.filter((_, i) => i !== idx) }));
  const updateItem = (idx: number, field: string, value: any) => {
    setFormData(p => ({ ...p, itens: p.itens.map((item, i) => i === idx ? { ...item, [field]: value } : item) }));
  };

  const selectProdutoServico = (idx: number, produto: ProdutoServico) => {
    const valorUnit = Number(produto.valorUnit);
    // Se valorUnit for 0 (produto com preço variável), usa precoMinimo como ponto de partida
    const valorInicial = valorUnit > 0 ? valorUnit : (Number(produto.precoMinimo) || 0);
    setFormData(p => ({
      ...p,
      itens: p.itens.map((item, i) => i === idx ? {
        ...item,
        produtoServicoId: produto.id,
        descricao: produto.nome + (produto.descricao ? ` - ${produto.descricao}` : ''),
        valorUnit: valorInicial,
        adicionalExecucao: valorUnit === 0, // marca como variável automaticamente
      } : item)
    }));
    setShowProdutoDropdown(null);
  };

  const calcTotal = () => {
    const subtotal = formData.itens.reduce((acc, i) => acc + (i.quantidade * i.valorUnit), 0);
    const desconto = subtotal * (formData.desconto / 100);
    const acrescimo = subtotal * (formData.acrescimo / 100);
    return subtotal - desconto + acrescimo;
  };

  const formatCurrency = (value: number) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
  const formatDate = (date: string) => {
    // Parse manual para evitar problemas de timezone
    const [year, month, day] = date.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };


  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? "bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20" : "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20"}`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg"><FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-xl font-bold text-foreground">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg"><Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Pendentes</p>
                <p className="text-xl font-bold text-foreground">{stats.pendentes}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg"><DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Recebido</p>
                <p className="text-xl font-bold text-foreground">{formatCurrency(Number(stats.valorRecebido))}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg"><AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Vencidas</p>
                <p className="text-xl font-bold text-foreground">{stats.vencidas}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="flex gap-4 flex-1">
          <select value={selectedClientId} onChange={(e) => { setSelectedClientId(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-input rounded-lg bg-background text-foreground">
            <option value="">Todos os clientes</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.razaoSocial}</option>)}
          </select>
          <select value={selectedStatus} onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-input rounded-lg bg-background text-foreground">
            <option value="">Todos os status</option>
            {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleGerarMensal}
            disabled={gerandoMensal}
            className="flex items-center gap-2 px-4 py-2 btn btn-outline"
            title="Gera faturas rascunho para todos os contratos ativos do mês atual"
          >
            {gerandoMensal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
            Gerar Faturas do Mês
          </button>
          <button onClick={openNewModal} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white rounded-[8px] shadow-[0_4px_12px_-2px_rgba(37,99,235,0.35)] hover:shadow-[0_8px_18px_-4px_rgba(37,99,235,0.45)] hover:-translate-y-0.5 transition-all duration-200">
            <Plus className="w-4 h-4" /> Nova Fatura
          </button>
        </div>
      </div>


      {/* Legenda de Ações */}
      <div className="bg-muted/30 border border-border rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Legenda de Ações</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-muted-foreground" />
            <span className="text-muted-foreground">Visualizar</span>
          </div>
          <div className="flex items-center gap-2">
            <FileDown className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span className="text-muted-foreground">Gerar PDF</span>
          </div>
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-muted-foreground">Publicar Portal</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-muted-foreground">Publicado</span>
          </div>
          <div className="flex items-center gap-2">
            <Edit2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-muted-foreground">Editar</span>
          </div>
          <div className="flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
            <span className="text-muted-foreground">Excluir</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
        ) : faturas.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">Nenhuma fatura encontrada</div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Nº</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Cliente</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Emissão</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Vencimento</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Valor</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {faturas.map((fatura) => (
                <Fragment key={fatura.id}>
                <tr className="hover:bg-muted/50">
                  <td className="px-4 py-3 text-sm font-medium text-foreground">{formatInvoiceNumber(fatura.numero)}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{fatura.client?.razaoSocial}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(fatura.dataEmissao)}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(fatura.dataVencimento)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-foreground">{formatCurrency(Number(fatura.valorTotal))}</td>
                  <td className="px-4 py-3 relative">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${statusLabels[fatura.status]?.color || "bg-muted text-muted-foreground"}`}>
                        {statusLabels[fatura.status]?.label || fatura.status}
                      </span>
                      {/* Botões de ação rápida baseados no status */}
                      {fatura.status === 'emitida' && (
                        <button
                          onClick={() => handleQuickStatusChange(fatura, 'enviada')}
                          className="p-1.5 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/10 rounded-lg transition-colors"
                          title="Marcar como Enviada"
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {fatura.status === 'enviada' && (
                        <button
                          onClick={() => handleQuickStatusChange(fatura, 'paga')}
                          className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                          title="Marcar como Paga"
                        >
                          <CheckCheck className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {/* Menu dropdown para mais opções */}
                      {(TRANSICOES_FATURA[fatura.status] ?? []).length > 0 && (
                        <div className="relative">
                          <button
                            onClick={() => setStatusMenuOpen(statusMenuOpen === fatura.id ? null : fatura.id)}
                            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                            title="Mais opções de status"
                          >
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                          {statusMenuOpen === fatura.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setStatusMenuOpen(null)} />
                              <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[140px] z-20">
                                {(TRANSICOES_FATURA[fatura.status] ?? []).map(status => (
                                  <button
                                    key={status}
                                    onClick={() => handleQuickStatusChange(fatura, status)}
                                    className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                                  >
                                    <span className={`w-2 h-2 rounded-full ${statusLabels[status]?.color.split(' ')[0]}`} />
                                    {statusLabels[status]?.label || status}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => openViewModal(fatura)}
                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg"
                        title="Visualizar"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleDocumentos(fatura)}
                        className={`p-2 rounded-lg ${expandedDocsFaturaId === fatura.id ? 'text-purple-600 dark:text-purple-400 bg-purple-500/15' : 'text-purple-600 dark:text-purple-400 hover:bg-purple-500/10'}`}
                        title="Documentos Vinculados (NFs/Boletos)"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try { await adminService.downloadFaturaPdf(fatura.id, (fatura as any).numero); }
                          catch { alert('Erro ao gerar PDF'); }
                        }}
                        className="p-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 rounded-lg"
                        title="Gerar PDF"
                      >
                        <FileDown className="w-4 h-4" />
                      </button>
                      <PublicarPortalButton
                        tipo="fatura"
                        documentoId={fatura.id}
                        variant="icon"
                        publicado={(fatura as any).publicadoPortal || false}
                        onSuccess={loadData}
                      />
                      <button
                        onClick={() => openEditModal(fatura)}
                        className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 rounded-lg"
                        title={['rascunho', 'emitida'].includes(fatura.status) ? 'Editar' : 'Visualizar'}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {['rascunho', 'cancelada'].includes(fatura.status) ? (
                        <button onClick={() => handleDelete(fatura.id)} className="p-2 text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded-lg" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                      ) : (
                        <button onClick={() => handleDeleteForced(fatura)} className="p-2 text-red-400 dark:text-red-300 opacity-60 hover:opacity-100 hover:bg-red-500/10 rounded-lg" title="Excluir (requer confirmação)"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedDocsFaturaId === fatura.id && (
                  <tr className="bg-muted/20">
                    <td colSpan={7} className="px-4 py-4">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <FolderOpen className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            Documentos Vinculados ({fatura.documentos?.length ?? 0})
                          </h4>
                          <button
                            type="button"
                            onClick={() => openAnexarModal(fatura)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs btn btn-primary"
                          >
                            <Paperclip className="w-3.5 h-3.5" />
                            Anexar Documento
                          </button>
                        </div>

                        {/* Documentos já vinculados */}
                        {(fatura.documentos ?? []).length === 0 ? (
                          <p className="text-sm text-muted-foreground py-3 text-center bg-muted/30 rounded-lg">
                            Nenhum documento vinculado a esta fatura
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {fatura.documentos!.map((doc: any) => (
                              <div key={doc.id} className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                                <div className="flex items-center gap-3">
                                  <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                  <div>
                                    <p className="text-sm font-medium text-foreground">{doc.nome}</p>
                                    <p className="text-xs text-muted-foreground">{doc.tipoDocumento?.nome}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => documentService.viewFile(doc.id)}
                                    className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 rounded-lg"
                                    title="Visualizar"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => documentService.downloadFile(doc.id, doc.arquivo)}
                                    className="p-2 text-muted-foreground hover:bg-muted rounded-lg"
                                    title="Download"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleUnlinkDocument(doc.id)}
                                    className="px-3 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded"
                                  >
                                    Desvincular
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Documentos disponíveis para vincular */}
                        <div>
                          <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                            Documentos Disponíveis
                          </h5>
                          {loadingDocumentos ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="w-5 h-5 text-brand animate-spin" />
                            </div>
                          ) : availableDocuments.filter((d: any) => !d.faturaId).length === 0 ? (
                            <p className="text-sm text-muted-foreground py-3 text-center bg-muted/30 rounded-lg">
                              Nenhum documento disponível para vincular
                            </p>
                          ) : (
                            <div className="space-y-2 max-h-56 overflow-y-auto">
                              {availableDocuments
                                .filter((d: any) => !d.faturaId)
                                .map((doc: any) => (
                                  <div key={doc.id} className="flex items-center justify-between p-3 bg-background border border-border rounded-lg hover:bg-muted/30">
                                    <div className="flex items-center gap-3">
                                      <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                      <div>
                                        <p className="text-sm font-medium text-foreground">{doc.nome}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {doc.tipoDocumento?.nome} • {formatDate(doc.dataReferencia)}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => handleLinkDocument(doc.id)}
                                      className="px-3 py-1 text-xs btn btn-primary"
                                    >
                                      Vincular
                                    </button>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table></div>
        )}
        
        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-border">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Anterior
            </button>
            <span className="text-muted-foreground text-sm">
              Página {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Próxima
            </button>
          </div>
        )}
      </div>


      {/* Modal Fatura */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-lg w-full max-w-6xl p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">{editingFatura ? "Editar Fatura" : "Nova Fatura"}</h3>
              <button onClick={closeModal} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            {editingFatura && !['rascunho', 'emitida'].includes(editingFatura.status) && (
              <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm text-yellow-700 dark:text-yellow-400">
                Fatura com status <strong>{statusLabels[editingFatura.status]?.label}</strong> — campos de dados somente leitura. Use a seção abaixo para alterar o status.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Cliente *</label>
                  <select value={formData.clientId} onChange={(e) => setFormData(p => ({ ...p, clientId: e.target.value }))} required
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground">
                    <option value="">Selecione</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.razaoSocial}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Forma de Pagamento</label>
                  <select value={formData.formaPagamento} onChange={(e) => setFormData(p => ({ ...p, formaPagamento: e.target.value }))}
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground">
                    <option value="">Selecione</option>
                    {formasPagamento.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Data Emissão *</label>
                  <input type="date" value={formData.dataEmissao} onChange={(e) => setFormData(p => ({ ...p, dataEmissao: e.target.value }))} required
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Data Vencimento *</label>
                  <input type="date" value={formData.dataVencimento} onChange={(e) => setFormData(p => ({ ...p, dataVencimento: e.target.value }))} required
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Desconto (%)</label>
                  <input type="number" step="0.01" min="0" max="100" value={formData.desconto} onChange={(e) => setFormData(p => ({ ...p, desconto: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Acréscimo (%)</label>
                  <input type="number" step="0.01" min="0" value={formData.acrescimo} onChange={(e) => setFormData(p => ({ ...p, acrescimo: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground" />
                </div>
              </div>

              {/* Itens */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-foreground">Itens</label>
                  <button type="button" onClick={addItem} className="text-sm text-brand hover:opacity-80">+ Adicionar Item</button>
                </div>
                <div className="space-y-2">
                  {/* Cabeçalho das colunas */}
                  <div className="hidden md:grid grid-cols-[110px_1fr_110px_130px_140px_120px_80px_40px] gap-3 px-3 pb-2 text-xs text-muted-foreground font-semibold border-b border-border/50">
                    <div className="text-center">Ação</div>
                    <div>Descrição</div>
                    <div className="text-center">Qtd.</div>
                    <div className="text-center">Vlr. Unit.</div>
                    <div className="text-center">Subtotal</div>
                    <div className="text-center">Tipo</div>
                    <div className="text-center" title="Marca o item como cobrança variável/adicional (não é mensalidade fixa)">Var.?</div>
                    <div></div>
                  </div>
                  {formData.itens.map((item, idx) => (
                    <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                      <div className="grid grid-cols-[110px_1fr_110px_130px_140px_120px_80px_40px] gap-3 items-center">
                        <div>
                          <button type="button" onClick={() => setShowProdutoDropdown(showProdutoDropdown === idx ? null : idx)}
                            className="w-full px-3 py-1.5 text-xs bg-brand/10 text-brand rounded hover:bg-brand/20 whitespace-nowrap">
                            <Search className="w-3 h-3 inline mr-1" />Buscar
                          </button>
                        </div>
                        <div className="relative">
                          <input type="text" placeholder="Descrição do item" value={item.descricao} onChange={(e) => updateItem(idx, "descricao", e.target.value)}
                            className="w-full px-2 py-1.5 border border-input rounded text-sm bg-background text-foreground" />
                          {showProdutoDropdown === idx && (
                            <ProdutoServicoSelector
                              produtos={produtosServicos}
                              onSelect={(p) => selectProdutoServico(idx, p)}
                              onClose={() => setShowProdutoDropdown(null)}
                            />
                          )}
                        </div>
                        <div>
                          <input type="number" placeholder="Qtd" step="any" min="0" value={item.quantidade} onChange={(e) => updateItem(idx, "quantidade", parseFloat(e.target.value) || 0)}
                            className="w-full px-2 py-1.5 border border-input rounded text-sm bg-background text-foreground text-center" />
                        </div>
                        <div>
                          <input type="number" placeholder="Valor" step="0.01" min="0" value={item.valorUnit} onChange={(e) => updateItem(idx, "valorUnit", parseFloat(e.target.value) || 0)}
                            className="w-full px-2 py-1.5 border border-input rounded text-sm bg-background text-foreground text-center" />
                        </div>
                        <div className="text-center text-sm font-semibold text-foreground">
                          {formatCurrency(item.quantidade * item.valorUnit)}
                        </div>
                        <div>
                          <select 
                            value={item.tipoItem || "proprio"} 
                            onChange={(e) => updateItem(idx, "tipoItem", e.target.value)}
                            className="w-full px-2 py-1.5 border border-input rounded text-sm bg-background text-foreground"
                          >
                            <option value="proprio">Próprio</option>
                            <option value="terceiros">Terceiros</option>
                            <option value="revenda">Revenda</option>
                          </select>
                        </div>
                        <div className="flex justify-center">
                          <input 
                            type="checkbox" 
                            checked={item.adicionalExecucao} 
                            onChange={(e) => updateItem(idx, "adicionalExecucao", e.target.checked)} 
                            className="cursor-pointer w-4 h-4"
                            title="Marque se este item é uma cobrança variável/adicional (ex.: consumo, execução extra). Mensalidades fixas devem ficar desmarcadas."
                          />
                        </div>
                        <div className="flex justify-center">
                          <button type="button" onClick={() => removeItem(idx)} className="p-1.5 text-red-500 dark:text-red-400 hover:bg-red-500/10 rounded">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-right text-lg font-bold text-foreground">
                  Total: {formatCurrency(calcTotal())}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Observações</label>
                <textarea value={formData.observacoes} onChange={(e) => setFormData(p => ({ ...p, observacoes: e.target.value }))} rows={2}
                  disabled={!!(editingFatura && !['rascunho', 'emitida'].includes(editingFatura.status))}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground disabled:opacity-60 disabled:cursor-not-allowed" />
              </div>

              <div className="flex gap-3 pt-4 border-t border-border">
                <button type="button" onClick={closeModal} className="flex-1 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" className="flex-1 py-2 btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Modal Status */}
      {showStatusModal && selectedFatura && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Alterar Status - Fatura {formatInvoiceNumber(selectedFatura.numero)}</h3>
              <button onClick={() => setShowStatusModal(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleStatusSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                <select value={statusForm.status} onChange={(e) => setStatusForm(p => ({ ...p, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground">
                  {(TRANSICOES_FATURA[selectedFatura.status] ?? []).map(k => (
                    <option key={k} value={k}>{statusLabels[k]?.label ?? k}</option>
                  ))}
                </select>
                {(TRANSICOES_FATURA[selectedFatura.status] ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground mt-1">Este status não permite mais transições.</p>
                )}
              </div>
              {statusForm.status === "paga" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Valor Pago</label>
                    <input type="number" step="0.01" value={statusForm.valorPago} onChange={(e) => setStatusForm(p => ({ ...p, valorPago: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Data do Pagamento</label>
                    <input type="date" value={statusForm.dataPagamento} onChange={(e) => setStatusForm(p => ({ ...p, dataPagamento: e.target.value }))}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground" />
                  </div>
                </>
              )}
              <div className="flex gap-3 pt-4 border-t border-border">
                <button type="button" onClick={() => setShowStatusModal(false)} className="flex-1 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" disabled={(TRANSICOES_FATURA[selectedFatura.status] ?? []).length === 0} className="flex-1 py-2 btn btn-primary disabled:opacity-50">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Modal Visualizar Fatura */}
      {showViewModal && viewingFatura && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Fatura {formatInvoiceNumber(viewingFatura.numero)}</h3>
              <button onClick={() => setShowViewModal(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              {/* Status badge */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusLabels[viewingFatura.status]?.color || 'bg-muted'}`}>
                  {statusLabels[viewingFatura.status]?.label || viewingFatura.status}
                </span>
                {viewingFatura.referenciaMes && (
                  <span className="text-sm text-muted-foreground">Referência: {viewingFatura.referenciaMes}</span>
                )}
              </div>

              {/* Cliente e datas */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground font-medium">Cliente</p>
                  <p className="text-foreground">{viewingFatura.client?.razaoSocial ?? '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">Emissão</p>
                  <p className="text-foreground">{formatDate(viewingFatura.dataEmissao)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">Vencimento</p>
                  <p className="text-foreground">{formatDate(viewingFatura.dataVencimento)}</p>
                </div>
                {viewingFatura.dataPagamento && (
                  <div>
                    <p className="text-muted-foreground font-medium">Pagamento</p>
                    <p className="text-foreground">{formatDate(viewingFatura.dataPagamento)}</p>
                  </div>
                )}
                {viewingFatura.formaPagamento && (
                  <div>
                    <p className="text-muted-foreground font-medium">Forma de Pagamento</p>
                    <p className="text-foreground capitalize">{viewingFatura.formaPagamento}</p>
                  </div>
                )}
              </div>

              {/* Valores */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left text-muted-foreground">Descrição</th>
                      <th className="px-3 py-2 text-center text-muted-foreground">Tipo</th>
                      <th className="px-3 py-2 text-right text-muted-foreground">Qtd</th>
                      <th className="px-3 py-2 text-right text-muted-foreground">Unit.</th>
                      <th className="px-3 py-2 text-right text-muted-foreground">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(viewingFatura.itens ?? []).map((item: any, idx: number) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-foreground">{item.descricao}</td>
                        <td className="px-3 py-2 text-center">
                          {item.tipoItem === 'terceiros' && (
                            <span className="inline-block bg-blue-500/20 text-blue-700 dark:text-blue-400 text-xs px-2 py-0.5 rounded">Terceiros</span>
                          )}
                          {item.tipoItem === 'revenda' && (
                            <span className="inline-block bg-orange-500/20 text-orange-700 dark:text-orange-400 text-xs px-2 py-0.5 rounded">Revenda</span>
                          )}
                          {(!item.tipoItem || item.tipoItem === 'proprio') && (
                            <span className="inline-block bg-green-500/20 text-green-700 dark:text-green-400 text-xs px-2 py-0.5 rounded">Próprio</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-foreground">{item.quantidade}</td>
                        <td className="px-3 py-2 text-right text-foreground">{formatCurrency(Number(item.valorUnit))}</td>
                        <td className="px-3 py-2 text-right text-foreground">{formatCurrency(item.quantidade * Number(item.valorUnit))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>

              {/* Totais */}
              <div className="space-y-1 text-sm text-right">
                {Number(viewingFatura.desconto) > 0 && (
                  <p className="text-muted-foreground">Desconto: -{viewingFatura.desconto}%</p>
                )}
                {Number(viewingFatura.acrescimo) > 0 && (
                  <p className="text-muted-foreground">Acréscimo: +{viewingFatura.acrescimo}%</p>
                )}
                <p className="text-lg font-bold text-foreground">Total: {formatCurrency(Number(viewingFatura.valorTotal))}</p>
                {viewingFatura.valorPago && (
                  <p className="text-green-600 dark:text-green-400">Valor Pago: {formatCurrency(Number(viewingFatura.valorPago))}</p>
                )}
              </div>

              {/* Observações */}
              {viewingFatura.observacoes && (
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Observações</p>
                  <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">{viewingFatura.observacoes}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4 border-t border-border mt-4">
              <button
                type="button"
                onClick={async () => {
                  try { await adminService.downloadFaturaPdf(viewingFatura.id, (viewingFatura as any).numero); }
                  catch { alert('Erro ao gerar PDF'); }
                }}
                className="flex items-center gap-2 px-4 py-2 btn btn-outline"
              >
                <FileDown className="w-4 h-4" /> Gerar PDF
              </button>
              <button
                type="button"
                onClick={async () => {
                  const statusLabel = statusLabels[viewingFatura.status]?.label ?? viewingFatura.status;
                  const msg = ['rascunho', 'cancelada'].includes(viewingFatura.status)
                    ? `Deseja excluir a fatura ${formatInvoiceNumber(viewingFatura.numero)}?`
                    : `Atenção: a fatura ${formatInvoiceNumber(viewingFatura.numero)} está com status "${statusLabel}".\n\nDeseja mesmo excluí-la?`;
                  if (!confirm(msg)) return;
                  try {
                    await adminService.deleteFatura(viewingFatura.id);
                    setShowViewModal(false);
                    setMessage({ type: 'success', text: 'Fatura excluída!' });
                    loadData();
                  } catch (err: any) {
                    setMessage({ type: 'error', text: err.response?.data?.error || 'Erro ao excluir fatura' });
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 btn btn-outline text-red-600 dark:text-red-400 border-red-500/30 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" /> Excluir
              </button>
              <button type="button" onClick={() => setShowViewModal(false)} className="ml-auto px-4 py-2 btn btn-primary">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Anexar Documento */}
      {showAnexarModal && anexarFatura && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                Anexar Documento - Fatura {formatInvoiceNumber(anexarFatura.numero)}
              </h3>
              <button onClick={() => setShowAnexarModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAnexarSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Tipo de Documento *</label>
                <select
                  value={anexarForm.tipoDocumentoId}
                  onChange={(e) => setAnexarForm((p) => ({ ...p, tipoDocumentoId: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                >
                  <option value="">Selecione um tipo</option>
                  {tiposDocumento.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Data de Referência *</label>
                <input
                  type="date"
                  value={anexarForm.dataReferencia}
                  onChange={(e) => setAnexarForm((p) => ({ ...p, dataReferencia: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome do Documento</label>
                <input
                  type="text"
                  value={anexarForm.nome}
                  onChange={(e) => setAnexarForm((p) => ({ ...p, nome: e.target.value }))}
                  placeholder="Ex: Boleto Jan/2026"
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${anexarFile ? "border-green-400 bg-green-500/10" : "border-input hover:border-brand/50 hover:bg-brand/5"}`}>
                {anexarFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-green-500" />
                    <p className="text-green-600 dark:text-green-400 font-medium text-sm">{anexarFile.name}</p>
                    <button type="button" onClick={() => setAnexarFile(null)} className="p-1 text-red-500 hover:bg-red-500/10 rounded-full" title="Remover">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <Upload className="w-6 h-6 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-sm font-medium text-foreground">Clique para selecionar o PDF</p>
                    <p className="text-xs text-muted-foreground">Apenas PDF, máximo 10MB</p>
                    <input type="file" accept=".pdf" onChange={handleAnexarFileChange} className="hidden" />
                  </label>
                )}
              </div>
              <div className="flex gap-3 pt-4 border-t border-border">
                <button type="button" onClick={() => setShowAnexarModal(false)} className="flex-1 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" disabled={uploading || !anexarFile} className="flex-1 py-2 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Enviando...</> : <><Upload className="w-4 h-4 inline mr-1" /> Anexar</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}